import { API_URL } from '@/environment';
import { useAuthedQuery } from '../core/useAuthedQuery';
import { queryKeys } from '../core/queryKeys';
import { FeatureAccess, User, UserData } from './types';
import { isSindiUserEmail, isTestUserEmail } from '@/config/testUsers';

async function getUser(
  authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>
): Promise<User> {
  const response = await authenticatedFetch(`${API_URL}/user`);

  if (!response.ok) {
    throw new Error(`Failed to fetch user: ${response.status}`);
  }

  const data = await response.json();
  return UserData.parse(data);
}

export function useUserQuery() {
  return useAuthedQuery<User, Error>(queryKeys.user, getUser);
}

export function useDevFeatureAccess(): boolean {
  const { data: user } = useUserQuery();

  if (!user?.email) {
    return false;
  }

  return isSindiUserEmail(user.email);
}

/** @public */
export function useTestUserAccess(): boolean {
  const { data: user } = useUserQuery();

  if (!user?.email) {
    return false;
  }

  return isSindiUserEmail(user.email) || isTestUserEmail(user.email);
}

/** @public */
export function useIsOrganizationOwner(): boolean {
  const { data: user } = useUserQuery();

  return user?.role === 'owner';
}

export function useIsSuperuser(): boolean {
  const { data: user } = useUserQuery();

  return user?.isSuperuser === true;
}

export function useFeatureAccess(): FeatureAccess | null {
  const { data: _user } = useUserQuery();

  return _user?.featureAccess ?? null;
}
